import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent,
  DialogTitle, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import {
  AllocationTargetsService,
  ALLOCATION_DIMENSIONS,
  CATEGORY_SUGGESTIONS,
  type AllocationDimension,
  type ClassificationUpsert,
} from '../../services/allocation-targets-service';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Distinct held symbols, from the portfolio payload already on the page. */
  symbols: string[];
  /** Called after a successful save so drift views can refresh. */
  onSaved?: () => void;
}

/**
 * Files held symbols under categories, one dimension at a time.
 *
 * Free text with suggestions rather than a fixed dropdown: the categories are
 * the household's vocabulary, and a taxonomy we invented would fit nobody.
 * Clearing a category removes the classification (the symbol returns to
 * "Unclassified") — absence of an answer, not a category of its own.
 */
export const ClassificationDialog: React.FC<Props> = ({ open, onClose, symbols, onSaved }) => {
  const [dimension, setDimension] = useState<AllocationDimension>('asset_class');
  // current[dimension][symbol] = category as loaded; edits[...] = as typed.
  const [current, setCurrent] = useState<Record<string, Record<string, string>>>({});
  const [edits, setEdits] = useState<Record<string, Record<string, string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    AllocationTargetsService.listClassifications()
      .then((rows) => {
        const byDim: Record<string, Record<string, string>> = {};
        for (const r of rows) {
          byDim[r.dimension] = byDim[r.dimension] ?? {};
          byDim[r.dimension][r.symbol] = r.category;
        }
        setCurrent(byDim);
        setEdits({});
        setError(null);
      })
      .catch((e) => setError(e?.message ?? 'Failed to load classifications'));
  }, [open]);

  const valueOf = (sym: string): string =>
    edits[dimension]?.[sym] ?? current[dimension]?.[sym] ?? '';

  const setValue = (sym: string, category: string) => {
    setEdits((prev) => ({
      ...prev,
      [dimension]: { ...prev[dimension], [sym]: category },
    }));
  };

  // Only rows that actually changed are sent: the endpoint upserts each
  // entry, and resending unchanged rows would churn updated_at for nothing.
  const changes = useMemo((): ClassificationUpsert[] => {
    const out: ClassificationUpsert[] = [];
    for (const [dim, bySymbol] of Object.entries(edits)) {
      for (const [symbol, category] of Object.entries(bySymbol)) {
        if ((current[dim]?.[symbol] ?? '') !== category) {
          out.push({ symbol, dimension: dim as AllocationDimension, category });
        }
      }
    }
    return out;
  }, [edits, current]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await AllocationTargetsService.putClassifications(changes);
      onSaved?.();
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save classifications');
    } finally {
      setSaving(false);
    }
  };

  const classified = symbols.filter((s) => valueOf(s) !== '').length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Classify symbols</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <ToggleButtonGroup
            size="small" exclusive value={dimension}
            onChange={(_, v) => v && setDimension(v)}
          >
            {ALLOCATION_DIMENSIONS.map((d) => (
              <ToggleButton key={d.value} value={d.value}>{d.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            {classified} of {symbols.length} classified
          </Typography>
        </Stack>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Symbol</TableCell>
              <TableCell sx={{ width: '60%' }}>Category</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {symbols.map((sym) => (
              <TableRow key={sym} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={650}>{sym}</Typography>
                </TableCell>
                <TableCell>
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={CATEGORY_SUGGESTIONS[dimension]}
                    value={valueOf(sym)}
                    // freeSolo fires onInputChange for typing and onChange for
                    // picks; handling both keeps typed-but-not-picked text.
                    onInputChange={(_, v) => setValue(sym, v)}
                    onChange={(_, v) => setValue(sym, v ?? '')}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Unclassified" variant="outlined" />
                    )}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => void save()}
          disabled={saving || changes.length === 0}
        >
          {saving ? 'Saving…' : `Save ${changes.length || ''}`.trim()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
